import { useEffect, useMemo } from 'react'
import { MULTISELECTION_SEPARATOR } from 'utils/constants'
import {
  DecisionModelAction,
  FlatInteractionModelData,
  SequenceDecisionModel
} from 'types/shared'
import { useAppDispatch } from 'state/store'
import { setStepsVisibility } from '../../redux'

type UseSequenceRulesProps = {
  activityModels: FlatInteractionModelData['activity_models']
  activityValues: FlatInteractionModelData['activity_values']
}

export default function useSequenceRules({
  activityModels,
  activityValues
}: UseSequenceRulesProps) {
  const dispatch = useAppDispatch()

  const sequenceDecisionModels = useMemo(() => {
    return Object.entries(activityModels || {}).reduce<
      Record<string, SequenceDecisionModel>
    >((result, [uuid, currentModel]) => {
      if (currentModel.sequence_decision_model) {
        result[uuid] = currentModel.sequence_decision_model
      }

      return result
    }, {})
  }, [activityModels])

  const { shownStepIds, hiddenStepIds, hasPendingDecisionModels } =
    useMemo(() => {
      const hiddenStepsSet = new Set<string>()
      const shownStepsSet = new Set<string>()

      const hasSequenceDecisionModels =
        Object.keys(sequenceDecisionModels).length > 0

      if (!activityValues || !hasSequenceDecisionModels) {
        return {
          shownStepIds: [],
          hiddenStepIds: [],
          hasPendingDecisionModels: hasSequenceDecisionModels
        }
      }

      let hasPendingDecisionModels = false

      const prioritizedActions: {
        activityModelUuid: string
        optionUuid: string
        action: DecisionModelAction
      }[] = Object.entries(sequenceDecisionModels)
        .flatMap(([uuid, sequenceDecisionModel]) => {
          return Object.entries(sequenceDecisionModel).flatMap(
            ([optionUuid, decisionModel]) =>
              decisionModel.map((action) => ({
                activityModelUuid: uuid,
                optionUuid,
                action
              }))
          )
        })
        .sort(({ action: a }, { action: b }) => {
          if (a.priority === b.priority) {
            return a.action === 'show_step' ? 1 : -1
          }
          return (a.priority || 0) - (b.priority || 0)
        })

      prioritizedActions.forEach(
        ({ activityModelUuid, action, optionUuid }) => {
          const value = activityValues[activityModelUuid]

          if (!value || Array.isArray(value)) {
            hasPendingDecisionModels = true

            return
          }

          const selections =
            typeof value === 'string'
              ? [value]
              : (value.id || value.des).split(MULTISELECTION_SEPARATOR)

          if (!selections.includes(optionUuid)) {
            return
          }

          if (action.action === 'show_step') {
            action.step_uuids?.forEach?.((stepUuid) => {
              hiddenStepsSet.delete(stepUuid)
              shownStepsSet.add(stepUuid)
            })
          }

          if (action.action === 'hide_step') {
            action.step_uuids?.forEach((stepUuid) => {
              shownStepsSet.delete(stepUuid)
              hiddenStepsSet.add(stepUuid)
            })
          }
        }
      )

      return {
        shownStepIds: Array.from(shownStepsSet),
        hiddenStepIds: Array.from(hiddenStepsSet),
        hasPendingDecisionModels
      }
    }, [activityValues, sequenceDecisionModels])

  useEffect(() => {
    dispatch(setStepsVisibility(shownStepIds, hiddenStepIds))
  }, [shownStepIds, hiddenStepIds, dispatch])

  return { hasPendingDecisionModels }
}
